import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
    Mail, 
    Sparkles,
    Shirt,
    Newspaper,
    BookOpen,
    Heart,
    Calendar,
    Wand2,
    Palette,
    Rocket
} from 'lucide-react';

// Custom Instagram icon (Lucide doesn't have brand icons)
const InstagramIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
);

// Custom TikTok icon
const TikTokIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
);

export default function Footer() {
    const currentYear = new Date().getFullYear();

    const quickLinks = [
        { name: 'Style Studio', path: '/style-studio', icon: Sparkles },
        { name: 'AI Studio', path: '/ai-studio', icon: Wand2 },
        { name: 'My Wardrobe', path: '/my-wardrobe', icon: Shirt },
        { name: 'Stylebook', path: '/lookbooks', icon: BookOpen },
        { name: 'Outfit Calendar', path: '/outfit-calendar', icon: Calendar },
        { name: 'Trend News', path: '/news', icon: Newspaper },
        { name: 'Picks of the Week', path: '/style-picks', icon: Palette },
        { name: 'Promoters', path: '/promoters', icon: Rocket },
    ];

    return (
        <footer className="animated-gradient text-stone-800 relative overflow-hidden border-t border-stone-200/60">
            {/* Subtle background pattern */}
            <div className="absolute inset-0 opacity-[0.03]" style={{
                backgroundImage: `radial-gradient(circle at 1px 1px, #78716c 1px, transparent 0)`,
                backgroundSize: '40px 40px'
            }} />

            {/* Main Footer Content */}
            <div className="max-w-7xl mx-auto px-4 lg:px-6 pt-10 lg:pt-16 pb-6 lg:pb-8 relative z-10">
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                    
                    {/* Brand Section */}
                    <div className="col-span-2 lg:col-span-1">
                        <Link to="/" className="inline-block mb-4 lg:mb-6">
                            <motion.div 
                                className="flex items-center gap-2"
                                whileHover={{ scale: 1.02 }}
                            >
                                <img 
                                    src="/logo-original.PNG" 
                                    alt="fashionDisc" 
                                    className="h-24 lg:h-56 w-auto object-contain"
                                />
                            </motion.div>
                        </Link>
                        <p className="text-stone-500 text-xs lg:text-sm leading-relaxed mb-4 lg:mb-6">
                            AI-powered fashion companion. Build your digital wardrobe, 
                            create outfits, and stay ahead of every trend.
                        </p>
                        
                        {/* Social Links */}
                        <div className="flex items-center gap-2 lg:gap-3">
                            <motion.a
                                href="https://instagram.com/fashiondisc.app"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 bg-stone-800/5 border border-stone-300/40 rounded-xl flex items-center justify-center text-stone-500 hover:text-white hover:bg-gradient-to-br hover:from-stone-600 hover:to-terracotta-600 hover:border-transparent transition-all duration-300"
                                whileHover={{ scale: 1.1, y: -2 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <InstagramIcon className="w-5 h-5" />
                            </motion.a>
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h3 className="text-stone-800 font-semibold mb-5 text-sm uppercase tracking-wider">
                            Features
                        </h3>
                        <ul className="space-y-3">
                            {quickLinks.map((link) => (
                                <li key={link.name}>
                                    <Link 
                                        to={link.path}
                                        className="group flex items-center gap-2 text-stone-500 hover:text-stone-900 transition-colors text-sm"
                                    >
                                        <link.icon className="w-4 h-4 text-stone-400 group-hover:text-terracotta-500 transition-colors" />
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact Section */}
                    <div>
                        <h3 className="text-stone-800 font-semibold mb-5 text-sm uppercase tracking-wider">
                            Contact
                        </h3>
                        <a 
                            href="mailto:fashiondisc25@gmail.com"
                            className="group flex items-start gap-3 text-stone-500 hover:text-stone-900 transition-colors"
                        >
                            <Mail className="w-5 h-5 mt-0.5 text-stone-400 group-hover:text-terracotta-500 transition-colors" />
                            <div>
                                <p className="text-sm">Email Us</p>
                                <p className="text-xs text-stone-400">fashiondisc25@gmail.com</p>
                            </div>
                        </a>

                        {/* Saved link with heart */}
                        <div className="mt-6 pt-6 border-t border-stone-300/40">
                            <Link 
                                to="/saved"
                                className="group flex items-center gap-2 text-stone-500 hover:text-stone-900 transition-colors text-sm"
                            >
                                <Heart className="w-4 h-4 text-stone-400 group-hover:text-terracotta-500 transition-colors" />
                                My Saved Items
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="mt-12 pt-8 border-t border-stone-300/40">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <p className="text-stone-400 text-sm">
                            © {currentYear} fashionDisc. All rights reserved.
                        </p>
                        <div className="flex items-center gap-4 text-stone-400 text-xs">
                            <a href="/privacy" className="hover:text-stone-700 transition-colors">Privacy Policy</a>
                            <a href="/terms" className="hover:text-stone-700 transition-colors">Terms of Service</a>
                            <span className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-olive-500" />
                                Powered by AI
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}
